import type { QuizReplacement, QuizRun, QuizStartTerms } from "@/lib/api/quizzes";

/** Learner copy for timed native quizzes (S4). `at` formats an instant in the course time zone. */
type At = (iso: string) => string;

const span = (minutes: number) => (minutes === 1 ? "1 minute" : `${minutes} minutes`);

/** The disclosure before Start: the time given, any close that cuts it, and when it would end. */
export function startTermsWords(terms: QuizStartTerms, at: At): string[] {
  if (terms.minutes === null) return terms.closes_at ? ["Untimed.", `Closes ${at(terms.closes_at)}. When it closes, the answers saved by then are handed in.`] : ["Untimed."];
  const lines = [terms.extra_percent ? `${span(terms.base_minutes ?? terms.minutes)}, with your extra time of ${terms.extra_percent}%: ${span(terms.minutes)} from Start.` : `${span(terms.minutes)} from Start.`];
  if (terms.cut_minutes > 0 && terms.closes_at) {
    lines.push(`The quiz closes ${at(terms.closes_at)}, before your time would end: starting now gives you ${span(terms.minutes - terms.cut_minutes)}, ${span(terms.cut_minutes)} less than ${terms.extra_percent ? "your allowance" : "the time limit"}.${terms.extra_percent ? " An allowance never moves a close." : ""}`);
  } else if (terms.closes_at) lines.push(`Closes ${at(terms.closes_at)}.`);
  if (terms.ends_at) lines.push(`Starting now, your time ends ${at(terms.ends_at)}. When it ends, the answers saved by then are handed in.`);
  return lines;
}

/** Whether a Start now is refused because the quiz has closed. */
export const closedNow = (terms: QuizStartTerms) => Boolean(terms.closes_at && Date.parse(terms.closes_at) <= Date.parse(terms.read_at));

/** The run's own end, while it runs and after the time ended. */
export function runTimeWords(run: QuizRun, at: At): string | null {
  if (run.expired_at) return `Your time ended ${at(run.expired_at)}. The answers saved by then were handed in.`;
  if (!run.ends_at || run.submitted_id || run.erased_at) return null;
  return `Your time ends ${at(run.ends_at)}. When it ends, the answers saved by then are handed in.`;
}

/** A replacement still to start: what it replaces and why, its time, and how it counts. Never the reason or who authorized it. */
export function replacementWords(replacement: QuizReplacement, at: At): string[] {
  const n = replacement.run_ordinal;
  return [
    `This replaces attempt ${n}. The teaching team authorized it because attempt ${n} started with extra time that wasn't the time recorded for you.`,
    `It asks attempt ${n}'s questions and takes its place, so it uses no new attempt.`,
    ...startTermsWords(replacement, at),
    `Once its result is released, the replacement counts in attempt ${n}'s place, even if its score is lower. If you don't start the replacement, attempt ${n} counts.`,
  ];
}

/** A replacement that can no longer start: why, and what counts instead. */
export function replacementClosedWords(replacement: QuizReplacement, at: At): string {
  const n = replacement.run_ordinal;
  if (replacement.lapsed_at) return `This replacement can no longer start: attempt ${n}'s released result was available to you, and the replacement asks the same questions. Attempt ${n}'s released result counts. If you need help, write to the teaching team in Private help.`;
  return `The replacement could start until ${at(replacement.start_blocked_at!)}. Attempt ${n}'s result counts once released. If you need help, write to the teaching team in Private help.`;
}

/** How an attempt with a replacement counts, read on either attempt. */
export function replacementCountWords(run: QuizRun, replacement: QuizReplacement | undefined): string | null {
  if (!replacement) return null;
  if (run.replaces_run_id) return `This replaces attempt ${replacement.run_ordinal}: once its result is released, it counts in attempt ${replacement.run_ordinal}'s place, even if lower.`;
  if (replacement.replacement_ordinal) return `Attempt ${replacement.replacement_ordinal} replaces it once its result is released; until then this result counts.`;
  return null;
}

export const heldResultWords = (ordinal: number) => `Attempt ${ordinal}'s result is released and held for you until you submit your replacement or its time to start ends: the replacement asks the same questions, so the score and comment wait too.`;
export const answersOffWords = "This quiz does not automatically show correct answers or explanations. Your instructor's comment appears as written and may discuss an answer.";
export const stoppedWords = (run: QuizRun, at: At) => run.stopped_at ? `Handed in ${at(run.stopped_at)} with the answers saved by then, when its replacement was authorized.` : null;
